import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

const USER_QUERY =
  "What is EAKC? Explain this to a complete non technical reader.";

const AI_RESPONSE_WORDS =
  "EAKC (Enterprise AI Knowledge Chat) is a smart AI tool designed like a personal assistant for small to medium-sized businesses (10-50 people) that helps them use their own documents, code, emails, and data without needing expensive big-company software.|1| Upload your stuff — drop in folders of files like marketing reports, code from GitHub, or client emails — no need to organize or label them. EAKC automatically figures out what's inside, like people, dates, deals, and connections.|2| Ask in plain English — say something simple like \"Summarize our latest client deals\" or \"Create a report and email it to the team.\" EAKC reads your data, understands it, and does the work.|3|";

interface SourceCard {
  file: string;
  category: string;
  match: string;
}

const SOURCE_CARDS: SourceCard[] = [
  { file: "eakc-platform-vision-and-roadmap.md", category: "Platform Vision", match: "100% match" },
  { file: "EAKC-strategy-and-reality-check.md", category: "Strategy", match: "100% match" },
  { file: "research/technical-deep-dive.md", category: "Research", match: "100% match" },
  { file: "2026-03-03-EAKC-ideas.md", category: "Ideas", match: "100% match" },
];

/** Parse response text with citation markers |N| into segments */
function parseResponse(text: string): Array<{ text: string; citation?: number }> {
  const segments: Array<{ text: string; citation?: number }> = [];
  const parts = text.split(/\|(\d+)\|/);
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      if (parts[i]) segments.push({ text: parts[i] });
    } else {
      segments.push({ text: "", citation: parseInt(parts[i], 10) });
    }
  }
  return segments;
}

const responseParsed = parseResponse(AI_RESPONSE_WORDS);

/** Get the plain text word count up to a frame, streaming 1 word per ~1.5 frames */
function getVisibleText(
  segments: Array<{ text: string; citation?: number }>,
  maxWords: number,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let wordCount = 0;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.citation !== undefined) {
      // Show citation only if preceding text is fully visible
      nodes.push(
        <sup
          key={`cite-${seg.citation}`}
          style={{
            fontFamily: FONTS.mono,
            fontSize: 11,
            color: COLORS.accentGreen,
            background: `${COLORS.accentGreen}26`,
            padding: "1px 4px",
            borderRadius: 4,
            marginLeft: 2,
            verticalAlign: "super",
          }}
        >
          {seg.citation}
        </sup>,
      );
      continue;
    }
    const words = seg.text.split(" ");
    for (let w = 0; w < words.length; w++) {
      if (wordCount >= maxWords) return nodes;
      if (wordCount > 0 || w > 0) nodes.push(" ");
      nodes.push(words[w]);
      wordCount++;
    }
  }
  return nodes;
}

export const Scene05AskAnything: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Typing the user query: frame 20-80
  const queryChars = Math.floor(
    interpolate(frame, [20, 80], [0, USER_QUERY.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
  const typedQuery = USER_QUERY.slice(0, queryChars);
  const showCursor = frame >= 20 && frame <= 85 && frame % 10 < 6;

  // AI response streaming: frame 100-400 (~2 words per 3 frames)
  const totalWords = AI_RESPONSE_WORDS.replace(/\|\d+\|/g, "")
    .split(" ")
    .filter(Boolean).length;
  const wordsVisible = Math.floor(
    interpolate(frame, [100, 400], [0, totalWords], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );

  // AI avatar appearance
  const aiAppear = spring({
    frame: frame - 95,
    fps,
    config: SPRING.snappy,
  });

  // Source panel cards staggered from frame 120
  const sourceCardOpacities = SOURCE_CARDS.map((_, i) =>
    interpolate(frame, [120 + i * 30, 150 + i * 30], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );

  // Fade out 860-900
  const fadeOut = interpolate(frame, [860, 900], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Container appearance
  const containerOpacity = interpolate(frame, [0, 20], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill>
      <GridBackground />

      <AbsoluteFill style={{ opacity: fadeOut }}>
        {/* Main container */}
        <div
          style={{
            position: "absolute",
            left: 260,
            top: 120,
            width: 1400,
            height: 750,
            background: COLORS.bgCard,
            border: `1px solid ${COLORS.borderLight}`,
            borderRadius: 16,
            display: "flex",
            overflow: "hidden",
            opacity: containerOpacity,
          }}
        >
          {/* Left: Chat area */}
          <div
            style={{
              flex: "0 0 940px",
              display: "flex",
              flexDirection: "column",
              borderRight: `1px solid ${COLORS.border}`,
            }}
          >
            {/* Chat header */}
            <div
              style={{
                padding: "16px 24px",
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 18,
                  fontWeight: 700,
                  color: COLORS.textPrimary,
                }}
              >
                EAKC Chat
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: COLORS.accentGreen,
                  background: `${COLORS.accentGreen}20`,
                  padding: "3px 10px",
                  borderRadius: 12,
                }}
              >
                Sources
              </span>
            </div>

            {/* Chat messages */}
            <div
              style={{
                flex: 1,
                padding: 24,
                display: "flex",
                flexDirection: "column",
                gap: 20,
                overflow: "hidden",
              }}
            >
              {/* User message */}
              {frame >= 20 && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div
                    style={{
                      maxWidth: 600,
                      background: `${COLORS.accentBlue}1F`,
                      border: `1px solid ${COLORS.accentBlue}40`,
                      borderRadius: 12,
                      padding: "14px 18px",
                      fontFamily: FONTS.body,
                      fontSize: 16,
                      color: COLORS.textPrimary,
                      lineHeight: 1.5,
                    }}
                  >
                    {typedQuery}
                    {showCursor && (
                      <span style={{ color: COLORS.accentBlue }}>|</span>
                    )}
                  </div>
                </div>
              )}

              {/* AI response */}
              {frame >= 95 && (
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    opacity: aiAppear,
                  }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    {"\uD83E\uDD16"}
                  </div>
                  <div style={{ flex: 1 }}>
                    {/* Heading */}
                    <div
                      style={{
                        fontFamily: FONTS.heading,
                        fontSize: 20,
                        fontWeight: 700,
                        color: COLORS.textPrimary,
                        marginBottom: 10,
                      }}
                    >
                      What is EAKC?
                    </div>
                    {/* Body */}
                    <div
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 15,
                        color: COLORS.textSecondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {getVisibleText(responseParsed, wordsVisible)}
                      {frame < 400 && frame % 12 < 7 && (
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 18,
                            background: COLORS.accentTeal,
                            borderRadius: 2,
                            marginLeft: 3,
                            verticalAlign: "text-bottom",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom input bar */}
            <div
              style={{
                padding: "14px 24px",
                borderTop: `1px solid ${COLORS.border}`,
              }}
            >
              <div
                style={{
                  background: COLORS.bg,
                  border: `1px solid ${COLORS.borderLight}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontFamily: FONTS.body,
                  fontSize: 14,
                  color: COLORS.textMuted,
                }}
              >
                Ask a question about your knowledge base...
              </div>
            </div>
          </div>

          {/* Right: Sources panel */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              background: COLORS.bg,
            }}
          >
            {/* Sources header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${COLORS.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.heading,
                  fontSize: 15,
                  fontWeight: 600,
                  color: COLORS.textPrimary,
                }}
              >
                {"\uD83D\uDCD1"} Sources (4)
              </span>
              <span
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 16,
                  color: COLORS.textMuted,
                  cursor: "pointer",
                }}
              >
                {"\u2715"}
              </span>
            </div>

            {/* Source cards */}
            <div
              style={{
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {SOURCE_CARDS.map((card, i) => (
                <div
                  key={card.file}
                  style={{
                    background: COLORS.bgCard,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                    padding: "14px 16px",
                    opacity: sourceCardOpacities[i],
                    transform: `translateY(${interpolate(
                      sourceCardOpacities[i],
                      [0, 1],
                      [10, 0],
                    )}px)`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 13,
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                      marginBottom: 4,
                    }}
                  >
                    {card.file}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        color: COLORS.textMuted,
                      }}
                    >
                      {card.category}
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 11,
                        color: COLORS.accentGreen,
                        background: `${COLORS.accentGreen}15`,
                        padding: "2px 8px",
                        borderRadius: 8,
                      }}
                    >
                      {card.match}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </AbsoluteFill>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
